/**
 * The client-report affordance gate (Red, 2026-07-28).
 *
 * Two surfaces offer "add to the client report" — SaveScreen and the decision
 * detail — and both are DISABLED until the two things below exist. One constant
 * so they cannot drift apart, and so turning the feature on is a single edit.
 *
 * WHY IT IS OFF. The PDF format is settled and the AC-1g-17 two-step confirm is
 * built, so it would be easy to mistake this for a finished feature that just
 * needs wiring. It is not. Two pieces are missing, and BOTH are load-bearing
 * because the client weekly is the one artifact that leaves the company:
 *
 *  1. **A resolver.** An item selected for the client report has to land in the
 *     right FIELD of that report. Nothing decides that today. It is either a
 *     hand-authored categorisation of every case or an LLM resolving it
 *     semantically — and if it is the LLM, the goalkeeper needs a deterministic
 *     guard against it placing an item in the wrong section, because "the AI put
 *     it in the wrong box" is not a defensible answer to a client.
 *
 *  2. **A goalkeeper-side blocklist, final and unoverridable.** A PM can select
 *     something containing "damage", "broken", "mistake", profanity, or anything
 *     else on a provided dictionary. That must NOT reach a client — and
 *     specifically it must not be releasable *even by the double confirm*. The
 *     two-step confirm protects against a slip; this protects against a
 *     judgement call the company would not stand behind. It therefore belongs in
 *     the Rust goalkeeper, not in this UI, where it cannot be clicked past.
 *
 * Until both exist the honest UI is a visibly disabled control that says so,
 * rather than a live button whose output has nowhere correct to go. This is the
 * same posture the server already takes: `report_digest` has no
 * `client_released` setter at all (TW-1i-RELEASE-SETTER), so the client render
 * is provably empty (AC-1i-14). The UI now matches that instead of implying
 * otherwise.
 *
 * NOT REMOVED, deliberately: the two-step confirm machinery behind these
 * controls stays built and stays tested. Rule #7 (two-step confirmation on every
 * client-facing send) is locked, and deleting the implementation would mean
 * rebuilding — and re-reviewing — it later.
 */

/** Flip to `true` only when the resolver AND the goalkeeper blocklist both ship. */
export const CLIENT_REPORT_ENABLED = false;

/** Shown when someone taps a disabled client-report control. Plain language, no
 * jargon (rule #10) — it says what is true and what to do instead. */
export const CLIENT_REPORT_COMING_SOON =
  "Client reports aren't turned on yet. Everything you mark still lands in the " +
  "internal daily log and the action list — nothing is lost. Flag it for the " +
  "daily or weekly report in the meantime.";
